<template>
  <Teleport to="body">
    <div v-if="isVisible" class="modal-overlay" :class="{ dark: isDark }" @click.stop>
      <div class="modal-content" @click.stop>
        <div class="header-container">
          <h2 class="modal-title">Pagar a Proveedor - {{ proveedor?.nombre_proveedor }}</h2>
          <button @click="$emit('close')" class="btn-close">&times;</button>
        </div>

        <div class="info-box">
          <div class="info-item"><strong>Proveedor:</strong> {{ proveedor?.nombre_proveedor }}</div>
          <div class="info-item"><strong>RTN:</strong> {{ proveedor?.rtn_proveedor }}</div>
          <div class="info-item">
            <strong>Total Deuda:</strong>
            <span class="deuda-total">{{ formatCurrency(proveedor?.saldo_pendiente_total) }}</span>
          </div>
        </div>

        <div class="facturas-section">
          <h3>Compras Pendientes de Pago</h3>
          <div v-if="cargando" class="loading">Cargando compras...</div>
          <div v-else-if="comprasPendientes.length === 0" class="no-data">
            No hay compras pendientes de pago
          </div>
          <div v-else class="facturas-list">
            <div
              v-for="compra in comprasPendientes"
              :key="compra.id_cuenta_pagar"
              class="factura-item"
              :class="{ selected: cuentasSeleccionadas.includes(compra.id_cuenta_pagar) }"
              @click="toggleSeleccion(compra.id_cuenta_pagar)"
            >
              <div class="factura-checkbox">
                <input
                  type="checkbox"
                  :checked="cuentasSeleccionadas.includes(compra.id_cuenta_pagar)"
                  @change="toggleSeleccion(compra.id_cuenta_pagar)"
                />
              </div>
              <div class="factura-info">
                <div class="factura-numero">{{ compra.numero_factura }}</div>
                <div class="factura-fecha">
                  {{ formatDate(compra.fecha_compra) }}
                  <span v-if="compra.fecha_vencimiento" class="vence">
                    · Vence: {{ formatDate(compra.fecha_vencimiento) }}
                  </span>
                </div>
              </div>
              <div class="factura-monto">{{ formatCurrency(compra.saldo_pendiente) }}</div>
            </div>
          </div>
        </div>

        <div class="resumen-pago">
          <div class="resumen-item">
            <span>Seleccionadas:</span>
            <span>{{ cuentasSeleccionadas.length }}</span>
          </div>
          <div class="resumen-item">
            <span>Total a Pagar:</span>
            <span class="total-pagar">{{ formatCurrency(totalAPagar) }}</span>
          </div>
        </div>

        <div class="pago-config">
          <div class="pago-buttons">
            <button @click="seleccionarTodas" class="btn btn-secondary" :disabled="comprasPendientes.length === 0">
              Seleccionar Todas
            </button>
            <button @click="limpiarSeleccion" class="btn btn-secondary" :disabled="cuentasSeleccionadas.length === 0">
              Limpiar
            </button>
          </div>

          <div class="form-group">
            <label>Monto a Pagar:</label>
            <div class="monto-controls">
              <input type="number" v-model.number="montoPago" :max="totalAPagar" :min="0.01" step="0.01" :disabled="procesando" class="monto-input" />
              <button @click="montoPago = totalAPagar" class="btn btn-small" :disabled="totalAPagar === 0">Total</button>
            </div>
            <div v-if="montoPago > totalAPagar" class="error-msg">El monto no puede superar el saldo pendiente</div>
          </div>

          <div class="form-group">
            <label>Método de Pago:</label>
            <select v-model="metodoPago" :disabled="procesando">
              <option value="Efectivo">Efectivo</option>
              <option value="Transferencia">Transferencia</option>
              <option value="Mixto">Mixto (Efectivo + Transferencia)</option>
            </select>
          </div>

          <div v-if="metodoPago === 'Transferencia'" class="form-group">
            <label>Número de transferencia:</label>
            <input type="text" v-model="numeroTransferencia" maxlength="20" placeholder="Número de transferencia" :disabled="procesando" />
          </div>

          <div v-if="metodoPago === 'Mixto'" class="mixto-fields">
            <div class="form-group">
              <label>Monto en Efectivo:</label>
              <input type="number" v-model.number="montoEfectivo" :max="montoPago" :min="0" step="0.01" :disabled="procesando" @input="calcularTransferencia" />
            </div>
            <div class="form-group">
              <label>Monto por Transferencia:</label>
              <input type="number" v-model.number="montoTransferencia" :max="montoPago" :min="0" step="0.01" :disabled="procesando" @input="calcularEfectivo" />
            </div>
            <div class="form-group">
              <label>Número de transferencia:</label>
              <input type="text" v-model="numeroTransferenciaMixto" maxlength="20" placeholder="Número de transferencia" :disabled="procesando" />
            </div>
          </div>

          <div class="form-group">
            <label>Notas:</label>
            <textarea v-model="notas" placeholder="Observaciones del pago..." :disabled="procesando"></textarea>
          </div>
        </div>

        <div class="button-container">
          <button @click="$emit('close')" class="btn btn-secondary" :disabled="procesando">Cancelar</button>
          <button @click="confirmarPago" :disabled="!pagoValido || procesando" class="btn btn-confirmar-pago">
            {{ procesando ? 'Procesando...' : 'Confirmar Pago' }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
import CuentasPorPagarService from "../../services/cuentasPorPagarSolicitudes";
import { useToast } from "vue-toastification";

export default {
  name: "PagarProveedorModal",
  props: {
    isVisible: { type: Boolean, required: true },
    proveedor: { type: Object, required: true },
    id_usuario: { type: Number, required: true },
  },
  emits: ['close', 'pago-realizado'],
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    return {
      isDark: false,
      procesando: false,
      cargando: false,
      comprasPendientes: [],
      cuentasSeleccionadas: [],
      montoPago: 0,
      metodoPago: "Efectivo",
      numeroTransferencia: "",
      montoEfectivo: 0,
      montoTransferencia: 0,
      numeroTransferenciaMixto: "",
      notas: "",
    };
  },
  computed: {
    totalAPagar() {
      return this.comprasPendientes
        .filter(c => this.cuentasSeleccionadas.includes(c.id_cuenta_pagar))
        .reduce((t, c) => t + c.saldo_pendiente, 0);
    },
    pagoValido() {
      const tieneSeleccion = this.cuentasSeleccionadas.length > 0;
      const montoOk = this.montoPago > 0 && this.montoPago <= this.totalAPagar;
      if (this.metodoPago === 'Transferencia') return tieneSeleccion && montoOk && this.numeroTransferencia.length >= 5;
      if (this.metodoPago === 'Mixto') {
        return tieneSeleccion && montoOk && this.montoEfectivo > 0 && this.montoTransferencia > 0 &&
          Math.abs(this.montoEfectivo + this.montoTransferencia - this.montoPago) < 0.01 &&
          this.numeroTransferenciaMixto.length >= 5;
      }
      return tieneSeleccion && montoOk;
    },
  },
  methods: {
    formatCurrency(v) { return `L. ${Number(v).toFixed(2)}`; },
    formatDate(d) { return d ? new Date(d).toLocaleDateString("es-HN") : ''; },

    async cargarComprasPendientes() {
      if (!this.proveedor?.id_proveedor) return;
      this.cargando = true;
      try {
        const data = await CuentasPorPagarService.obtenerComprasPendientesPorProveedor(
          this.proveedor.id_proveedor, this.id_usuario
        );
        this.comprasPendientes = data || [];
      } catch (e) {
        this.toast.error("Error al cargar compras pendientes: " + e.message);
      } finally {
        this.cargando = false;
      }
    },

    toggleSeleccion(id) {
      const idx = this.cuentasSeleccionadas.indexOf(id);
      if (idx > -1) this.cuentasSeleccionadas.splice(idx, 1);
      else this.cuentasSeleccionadas.push(id);
      this.montoPago = this.totalAPagar;
    },
    seleccionarTodas() {
      this.cuentasSeleccionadas = this.comprasPendientes.map(c => c.id_cuenta_pagar);
      this.montoPago = this.totalAPagar;
    },
    limpiarSeleccion() { this.cuentasSeleccionadas = []; this.montoPago = 0; },
    calcularTransferencia() { this.montoTransferencia = Math.max(0, this.montoPago - (this.montoEfectivo || 0)); },
    calcularEfectivo() { this.montoEfectivo = Math.max(0, this.montoPago - (this.montoTransferencia || 0)); },

    async confirmarPago() {
      if (!this.pagoValido || this.procesando) return;
      this.procesando = true;
      try {
        const datos = {
          cuentas_seleccionadas: this.cuentasSeleccionadas,
          monto_pago: this.montoPago,
          metodo_pago: this.metodoPago,
          notas: this.notas,
        };
        if (this.metodoPago === 'Transferencia') datos.numero_transferencia = this.numeroTransferencia;
        if (this.metodoPago === 'Mixto') {
          datos.monto_efectivo = this.montoEfectivo;
          datos.monto_transferencia = this.montoTransferencia;
          datos.numero_transferencia = this.numeroTransferenciaMixto;
        }
        const resp = await CuentasPorPagarService.registrarPagoProveedor(datos, this.id_usuario);
        if (resp.success) {
          this.toast.success("Pago a proveedor registrado exitosamente");
          this.$emit('pago-realizado');
        } else {
          throw new Error(resp.message || "Error al procesar el pago");
        }
      } catch (e) {
        this.toast.error(e.message || "Error al procesar el pago");
      } finally {
        this.procesando = false;
      }
    },

    resetModal() {
      this.comprasPendientes = [];
      this.cuentasSeleccionadas = [];
      this.montoPago = 0;
      this.metodoPago = "Efectivo";
      this.numeroTransferencia = "";
      this.montoEfectivo = 0;
      this.montoTransferencia = 0;
      this.numeroTransferenciaMixto = "";
      this.notas = "";
    },
  },
  watch: {
    isVisible(val) {
      if (val) {
        this.resetModal();
        this.cargarComprasPendientes();
      }
    },
    metodoPago() {
      this.numeroTransferencia = "";
      this.montoEfectivo = 0;
      this.montoTransferencia = 0;
      this.numeroTransferenciaMixto = "";
    },
  },
  mounted() {
    this.isDark = localStorage.getItem('isDarkMode') === 'true';
    this._onToggleDark = () => { this.isDark = localStorage.getItem('isDarkMode') === 'true'; };
    window.addEventListener('poswave:toggle-dark', this._onToggleDark);
    if (this.isVisible) {
      this.resetModal();
      this.cargarComprasPendientes();
    }
  },
  beforeUnmount() {
    window.removeEventListener('poswave:toggle-dark', this._onToggleDark);
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,100..900;1,100..900&display=swap");
* { font-family: "Montserrat", sans-serif; box-sizing: border-box; }

.modal-overlay {
  position: fixed; inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex; align-items: flex-start; justify-content: center;
  padding: 2vh 1rem; z-index: 9100; overflow-y: auto;
}
.modal-content {
  background: white; border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.2);
  width: 100%; max-width: 560px; margin: auto;
  display: flex; flex-direction: column; overflow: hidden;
  animation: slideUp 0.2s ease;
}
@keyframes slideUp {
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
}

.header-container {
  display: flex; align-items: center; justify-content: space-between;
  padding: 1rem 1.25rem; border-bottom: 1px solid #dee2e6; background: #f8f9fa;
}
.modal-title { font-size: 1rem; font-weight: 700; margin: 0; color: #212529; }
.btn-close {
  background: none; border: none; font-size: 1.4rem; cursor: pointer;
  color: #6c757d; padding: 4px; width: 32px; height: 32px;
  display: flex; align-items: center; justify-content: center;
  border-radius: 6px; transition: all 0.2s ease;
}
.btn-close:hover { background: #e9ecef; color: #212529; }

.info-box {
  background: #f8f9fa; padding: 15px; border-bottom: 1px solid #dee2e6;
  display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 12px;
}
.info-item { font-size: 13px; color: #495057; }
.info-item strong { color: #212529; display: block; margin-bottom: 2px; }
.deuda-total { color: #dc3545; font-weight: 700; font-size: 15px; }

.facturas-section { padding: 1rem 1.25rem; }
.facturas-section h3 { margin: 0 0 12px; color: #212529; font-size: 0.9375rem; font-weight: 700; }
.loading, .no-data { text-align: center; padding: 20px; color: #6c757d; font-style: italic; }
.facturas-list { max-height: 260px; overflow-y: auto; border: 1px solid #dee2e6; border-radius: 8px; }
.factura-item {
  display: flex; align-items: center; gap: 12px;
  padding: 10px 14px; border-bottom: 1px solid #f0f0f0;
  cursor: pointer; transition: background 0.15s;
}
.factura-item:last-child { border-bottom: none; }
.factura-item:hover { background: #f8f9fa; }
.factura-item.selected { background: #fffaf2; border-left: 3px solid #c09d62; }
.factura-checkbox input { width: 16px; height: 16px; cursor: pointer; accent-color: #c09d62; }
.factura-info { flex: 1; }
.factura-numero { font-weight: 600; font-size: 13px; color: #212529; }
.factura-fecha { font-size: 12px; color: #6c757d; margin-top: 2px; }
.vence { color: #dc3545; }
.factura-monto { font-weight: 700; color: #212529; font-size: 13px; }

.resumen-pago {
  background: #fff3cd; border-top: 1px solid #dee2e6;
  padding: 10px 1.25rem; display: flex; gap: 20px;
}
.resumen-item { font-size: 13px; color: #495057; display: flex; gap: 6px; align-items: center; }
.total-pagar { font-weight: 700; color: #856404; font-size: 15px; }

.pago-config { padding: 1rem 1.25rem; display: flex; flex-direction: column; gap: 12px; }
.pago-buttons { display: flex; gap: 8px; }
.form-group { display: flex; flex-direction: column; gap: 4px; }
.form-group label { font-size: 0.8rem; font-weight: 600; color: #495057; }
.monto-controls { display: flex; gap: 8px; }
.monto-controls input { flex: 1; }
input, select, textarea {
  width: 100%; padding: 8px 12px; border: 1px solid #ced4da;
  border-radius: 8px; font-size: 0.875rem; font-family: "Montserrat", sans-serif;
  color: #212529; background: white; outline: none;
  transition: border-color 0.2s, box-shadow 0.2s;
}
input:focus, select:focus, textarea:focus {
  border-color: #c09d62; box-shadow: 0 0 0 3px rgba(192,157,98,0.15);
}
input:disabled, select:disabled, textarea:disabled { background: #f8f9fa; opacity: 0.7; }
textarea { resize: vertical; min-height: 64px; }
.error-msg { color: #dc3545; font-size: 12px; }
.mixto-fields { display: flex; flex-direction: column; gap: 10px; background: #f8f9fa; padding: 12px; border-radius: 8px; border: 1px solid #dee2e6; }

.button-container {
  display: flex; gap: 8px; justify-content: flex-end;
  padding: 1rem 1.25rem; border-top: 1px solid #dee2e6; background: #f8f9fa;
}
.btn { display: inline-flex; align-items: center; gap: 6px; padding: 8px 16px; border: none; border-radius: 8px; font-size: 0.875rem; font-weight: 600; cursor: pointer; font-family: "Montserrat", sans-serif; transition: all 0.2s ease; }
.btn:disabled { opacity: 0.5; cursor: not-allowed; }
.btn-confirmar-pago { background-color: #198754; color: white; }
.btn-confirmar-pago:hover:not(:disabled) { background-color: #146c43; }
.btn-secondary { background-color: #6c757d; color: white; }
.btn-secondary:hover:not(:disabled) { background-color: #5a6268; }
.btn-small { padding: 6px 10px; font-size: 0.8rem; background: #e9ecef; color: #495057; border: 1px solid #dee2e6; border-radius: 6px; cursor: pointer; }
.btn-small:hover { background: #dee2e6; }

@media (max-width: 560px) {
  .info-box { grid-template-columns: 1fr; }
  .modal-content { margin: 0; border-radius: 0; }
}

/* ── Dark mode ─────────────────────────────────────────────── */
.dark .modal-content {
  background: #2d2d2d;
  color: #ffffff;
}
.dark .header-container {
  background: #1e1e1e;
  border-color: #404040;
}
.dark .modal-title { color: #ffffff; }
.dark .btn-close { color: #ffffff; }
.dark .btn-close:hover { background-color: #404040; color: #ffffff; }

.dark .info-box {
  background: #1e1e1e;
  border-color: #404040;
}
.dark .info-item { color: #ffffff; }
.dark .info-item strong { color: #aaaaaa; }
.dark .deuda-total { color: #ff6b6b; }

.dark .facturas-section h3 { color: #ffffff; }
.dark .loading, .dark .no-data { color: #aaaaaa; }
.dark .facturas-list { border-color: #404040; }
.dark .factura-item { border-color: #404040; }
.dark .factura-item:hover { background: #3a3a3a; }
.dark .factura-item.selected { background: #1a3a52; border-left-color: #4a9eff; }
.dark .factura-numero { color: #ffffff; }
.dark .factura-fecha { color: #aaaaaa; }
.dark .vence { color: #ff6b6b; }
.dark .factura-monto { color: #4a9eff; }

.dark .resumen-pago { background: #1e1e1e; border-color: #404040; }
.dark .resumen-item { color: #ffffff; }
.dark .total-pagar { color: #4caf50; }

.dark .form-group label { color: #ffffff; }
.dark input, .dark select, .dark textarea {
  background: #2d2d2d;
  border-color: #404040;
  color: #ffffff;
}
.dark input:focus, .dark select:focus, .dark textarea:focus {
  border-color: #4a9eff;
  box-shadow: 0 0 0 3px rgba(74,158,255,0.15);
}
.dark input:disabled, .dark select:disabled, .dark textarea:disabled {
  background: #1e1e1e;
}
.dark .error-msg { color: #ff6b6b; }
.dark .mixto-fields { background: #1e1e1e; border-color: #404040; }

.dark .button-container { background: #1e1e1e; border-color: #404040; }
.dark .btn-small { background: #404040; color: #ffffff; border-color: #505050; }
.dark .btn-small:hover { background: #505050; }
</style>